

import { Idea, IdeaVersion, UserProfile, PlanTier, PLAN_LIMITS, MonthlyAllowance } from '../types';
import { db, auth, storage } from './firebase';
import { collection, query, where, getDocs, setDoc, deleteDoc, doc, getDoc, orderBy, addDoc, writeBatch, increment, updateDoc, limit } from 'firebase/firestore';
import { ref, uploadString, getDownloadURL } from 'firebase/storage';
import { User } from 'firebase/auth';

// Abstract Interface for Storage Providers
export interface StorageProvider {
  getIdeas(): Promise<Idea[]>;
  getPublicIdeas(): Promise<Idea[]>;
  saveIdea(idea: Idea): Promise<void>;
  deleteIdea(id: string): Promise<void>;
  archiveIdea(id: string, archive: boolean): Promise<void>; // Strict boolean
  getIdeaVersions(ideaId: string): Promise<IdeaVersion[]>;
  
  // User Profile & Usage
  getUserProfile(userId: string, email: string): Promise<UserProfile>;
  updateUserProfile(userId: string, data: Partial<UserProfile>): Promise<void>;
  incrementUsage(userId: string, type: 'ideas' | 'tokens', amount: number): Promise<void>;
  getMonthlyAllowance(userId: string): Promise<MonthlyAllowance>;
  upgradeUserPlan(userId: string, plan: PlanTier): Promise<MonthlyAllowance>;
}

// --- Helper: UTC Month Key ---
const getCurrentUTCMonthKey = (): string => {
  const d = new Date();
  const year = d.getUTCFullYear();
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
};

// --- Firestore Implementation ---
class FirebaseProvider implements StorageProvider {
  
  private getUser() {
    return auth.currentUser;
  }

  async getIdeas(): Promise<Idea[]> {
    const user = this.getUser();
    if (!user) return [];

    try {
        const q = query(collection(db, "ideas"), where("userId", "==", user.uid));
        const querySnapshot = await getDocs(q);
        
        const ideas: Idea[] = [];
        querySnapshot.forEach((doc) => {
          ideas.push(doc.data() as Idea);
        });
        return ideas;
    } catch (e: any) {
        console.error(`[Firestore Error] Operation: getIdeas. Collection: ideas. User: ${user.uid}`, e);
        throw e;
    }
  }

  async getPublicIdeas(): Promise<Idea[]> {
      try {
          const q = query(
              collection(db, "ideas"), 
              where("isPublic", "==", true),
              where("isArchived", "==", false),
              orderBy("createdAt", "desc"),
              limit(50)
          );
          
          const querySnapshot = await getDocs(q);
          const ideas: Idea[] = [];
          querySnapshot.forEach((doc) => {
              ideas.push(doc.data() as Idea);
          });
          return ideas;
      } catch (e: any) {
          console.error("[Firestore Error] Operation: getPublicIdeas. Collection: ideas.", e);
          return [];
      }
  }

  // Helper to upload base64 images to Firebase Storage
  private async uploadBase64Image(userId: string, ideaId: string, base64Data: string, prefix: string): Promise<string> {
    if (!base64Data || base64Data.startsWith('http')) return base64Data;

    const timestamp = Date.now();
    const random = Math.floor(Math.random() * 1000);
    const storagePath = `users/${userId}/ideas/${ideaId}/${prefix}_${timestamp}_${random}.png`;
    const storageRef = ref(storage, storagePath);

    try {
        await uploadString(storageRef, base64Data, 'data_url');
        return await getDownloadURL(storageRef);
    } catch (e: any) {
        console.error(`[Storage Error] Operation: uploadImage. Path: ${storagePath}`, e);
        if (e.code === 'storage/unauthorized') {
             throw new Error("Permission denied: Unable to upload image. Please check Firebase Storage Rules.");
        }
        throw new Error("Failed to upload image to storage.");
    }
  }

  async saveIdea(idea: Idea): Promise<void> {
    const user = this.getUser();
    if (!user) throw new Error("User must be logged in to save");

    const ideaId = idea.id;
    const userId = user.uid;

    try {
        let logoUrl = idea.logo;
        if (logoUrl && logoUrl.startsWith('data:')) {
            logoUrl = await this.uploadBase64Image(userId, ideaId, logoUrl, 'logo');
        }

        const landingPages = await Promise.all(
            (idea.landingPageDesigns || []).map((img, idx) => 
                img.startsWith('data:') 
                    ? this.uploadBase64Image(userId, ideaId, img, `landing_${idx}`) 
                    : img
            )
        );

        const concepts = await Promise.all(
            (idea.productConcepts || []).map((img, idx) => 
                img.startsWith('data:') 
                    ? this.uploadBase64Image(userId, ideaId, img, `concept_${idx}`) 
                    : img
            )
        );

        const rawIdeaToSave: any = { 
            ...idea, 
            userId: user.uid, // Strict Enforcement
            isPublic: !!idea.isPublic, // Ensure boolean
            logo: logoUrl || null,
            landingPageDesigns: landingPages,
            productConcepts: concepts,
            landingPageDesign: landingPages[0] || null
        };
        
        const ideaToSave = JSON.parse(JSON.stringify(rawIdeaToSave));
        
        const batch = writeBatch(db);
        const ideaRef = doc(db, "ideas", ideaId);
        batch.set(ideaRef, ideaToSave);

        // Version History
        const versionRef = doc(collection(db, "ideas", ideaId, "versions"));
        const versionData: IdeaVersion = {
            id: versionRef.id,
            ideaId: ideaId,
            savedAt: Date.now(),
            data: ideaToSave // Contains userId, important for rules
        };
        batch.set(versionRef, JSON.parse(JSON.stringify(versionData)));
        
        await batch.commit();

    } catch (e: any) {
        console.error(`[Firestore Error] Operation: saveIdea (Batch). Collection: ideas/versions. ID: ${ideaId}. User: ${userId}`, e);
        if (e.code === 'resource-exhausted') {
            throw new Error("Idea is too large to save. Images may have failed to upload to storage.");
        }
        if (e.code === 'permission-denied') {
            throw new Error("Permission denied. Check Firestore rules.");
        }
        throw e;
    }
  }

  async deleteIdea(id: string): Promise<void> {
    const user = this.getUser();
    if (!user) throw new Error("User not logged in");
    try {
        await deleteDoc(doc(db, "ideas", id));
    } catch (e: any) {
        console.error(`[Firestore Error] Operation: deleteIdea. ID: ${id}`, e);
        throw e;
    }
  }

  async archiveIdea(id: string, archive: boolean): Promise<void> {
    const user = this.getUser();
    if (!user) throw new Error("User not logged in");

    try {
        const ideaRef = doc(db, "ideas", id);
        const snap = await getDoc(ideaRef);
        
        if (snap.exists()) {
            const data = snap.data() as Idea;
            if (data.userId === user.uid) {
                const updated = { ...data, isArchived: archive, updatedAt: Date.now() };
                await setDoc(ideaRef, updated);
            }
        }
    } catch (e: any) {
        console.error(`[Firestore Error] Operation: archiveIdea. ID: ${id}`, e);
        throw e;
    }
  }

  async getIdeaVersions(ideaId: string): Promise<IdeaVersion[]> {
      const user = this.getUser();
      if (!user) return [];

      try {
        const q = query(collection(db, "ideas", ideaId, "versions"), orderBy("savedAt", "desc"));
        const snap = await getDocs(q);
        const versions: IdeaVersion[] = [];
        snap.forEach(d => {
            const v = d.data() as IdeaVersion;
            v.id = d.id;
            versions.push(v);
        });
        return versions;
      } catch (e: any) {
          console.error(`[Firestore Error] Operation: getIdeaVersions. ID: ${ideaId}`, e);
          return [];
      }
  }

  // --- User Profile & Usage ---

  async getUserProfile(userId: string, email: string): Promise<UserProfile> {
    const userRef = doc(db, "users", userId);
    try {
        const snap = await getDoc(userRef);

        if (snap.exists()) {
        return snap.data() as UserProfile;
        } else {
        // Initialize new profile
        const newProfile: UserProfile = {
            uid: userId,
            email: email,
            plan: 'free',
            ideasCreated: 0,
            tokensUsed: 0,
            subscriptionStatus: 'active',
            periodEnd: Date.now() + 1000 * 60 * 60 * 24 * 365
        };
        await setDoc(userRef, newProfile);
        return newProfile;
        }
    } catch (e: any) {
        console.error(`[Firestore Error] Operation: getUserProfile. User: ${userId}`, e);
        throw e;
    }
  }

  async getMonthlyAllowance(userId: string): Promise<MonthlyAllowance> {
    const monthKey = getCurrentUTCMonthKey();
    const allowanceRef = doc(db, `users/${userId}/apps/idea-shelf/allowance/${monthKey}`);
    
    try {
        const snap = await getDoc(allowanceRef);
        if (snap.exists()) {
            return snap.data() as MonthlyAllowance;
        }

        // Initialize if missing
        const userRef = doc(db, "users", userId);
        const userSnap = await getDoc(userRef);
        const profile = userSnap.exists() ? userSnap.data() as UserProfile : null;
        
        let plan = profile?.plan || 'free';
        if (!PLAN_LIMITS[plan]) plan = 'free';

        const limits = PLAN_LIMITS[plan];
        
        const newAllowance: MonthlyAllowance = {
            limit: limits.credits,
            used: 0,
            plan: plan,
            resetAt: Date.now()
        };
        
        await setDoc(allowanceRef, newAllowance);
        return newAllowance;
    } catch (e: any) {
        console.error(`[Firestore Error] Operation: getMonthlyAllowance. User: ${userId}`, e);
        // Fallback to offline/memory allowance to prevent app crash
        return { limit: 10000, used: 0, plan: 'free', resetAt: Date.now() };
    }
  }

  async updateUserProfile(userId: string, data: Partial<UserProfile>): Promise<void> {
    try {
        const userRef = doc(db, "users", userId);
        await updateDoc(userRef, data);
    } catch (e: any) {
        console.error(`[Firestore Error] Operation: updateUserProfile. User: ${userId}`, e);
        throw e;
    }
  }

  async incrementUsage(userId: string, type: 'ideas' | 'tokens', amount: number): Promise<void> {
    try {
        const userRef = doc(db, "users", userId);
        const batch = writeBatch(db);

        // 1. Update Lifetime Stats in User Profile
        const field = type === 'ideas' ? 'ideasCreated' : 'tokensUsed';
        batch.update(userRef, { [field]: increment(amount) });

        // 2. Update Monthly Allowance if 'tokens' (Credits)
        if (type === 'tokens') {
            const monthKey = getCurrentUTCMonthKey();
            const allowanceRef = doc(db, `users/${userId}/apps/idea-shelf/allowance/${monthKey}`);
            batch.set(allowanceRef, { used: increment(amount) }, { merge: true });
        }

        await batch.commit();
    } catch (e: any) {
        console.error(`[Firestore Error] Operation: incrementUsage. User: ${userId}`, e);
        throw e;
    }
  }

  async upgradeUserPlan(userId: string, plan: PlanTier): Promise<MonthlyAllowance> {
      try {
        const batch = writeBatch(db);
        const userRef = doc(db, "users", userId);
        batch.update(userRef, { plan });

        const monthKey = getCurrentUTCMonthKey();
        const allowanceRef = doc(db, `users/${userId}/apps/idea-shelf/allowance/${monthKey}`);
        
        const newLimits = PLAN_LIMITS[plan];
        
        batch.set(allowanceRef, { 
            limit: newLimits.credits,
            plan: plan
        }, { merge: true });

        await batch.commit();

        const snap = await getDoc(allowanceRef);
        if (!snap.exists()) {
            return { limit: newLimits.credits, used: 0, plan, resetAt: Date.now() };
        }
        return snap.data() as MonthlyAllowance;
      } catch (e: any) {
        console.error(`[Firestore Error] Operation: upgradeUserPlan. User: ${userId}`, e);
        throw e;
      }
  }
}

// --- IndexedDB Implementation (Fallback) ---
// Simplified fallback that doesn't fully support monthly logic yet, acts as offline cache
class IndexedDBProvider implements StorageProvider {
  private dbPromise: Promise<IDBDatabase>;

  constructor() {
    this.dbPromise = new Promise((resolve, reject) => {
      // Check for server-side environment or missing IDB
      if (typeof window === 'undefined' || !window.indexedDB) {
          return;
      }
      const request = indexedDB.open('IdeaShelfDB', 3);
      request.onerror = () => reject(request.error);
      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains('ideas')) db.createObjectStore('ideas', { keyPath: 'id' });
        if (!db.objectStoreNames.contains('idea_versions')) {
            const vStore = db.createObjectStore('idea_versions', { keyPath: 'id' });
            vStore.createIndex('ideaId', 'ideaId', { unique: false });
        }
        if (!db.objectStoreNames.contains('users')) db.createObjectStore('users', { keyPath: 'uid' });
      };
      request.onsuccess = () => resolve(request.result);
    });
  }

  private async getTransaction(stores: string[], mode: IDBTransactionMode) {
      const db = await this.dbPromise;
      return db.transaction(stores, mode);
  }

  async getIdeas(): Promise<Idea[]> {
    const db = await this.dbPromise;
    return new Promise((resolve) => {
      const store = db.transaction('ideas', 'readonly').objectStore('ideas');
      const req = store.getAll();
      req.onsuccess = () => resolve(req.result || []);
    });
  }
  
  async getPublicIdeas(): Promise<Idea[]> { return []; }
  
  async saveIdea(idea: Idea): Promise<void> {
    const tx = await this.getTransaction(['ideas', 'idea_versions'], 'readwrite');
    tx.objectStore('ideas').put(JSON.parse(JSON.stringify(idea)));
    tx.objectStore('idea_versions').put({ id: `${idea.id}_${Date.now()}`, ideaId: idea.id, savedAt: Date.now(), data: idea });
    return new Promise(resolve => { tx.oncomplete = () => resolve(); });
  }

  async deleteIdea(id: string): Promise<void> {
    const tx = await this.getTransaction(['ideas'], 'readwrite');
    tx.objectStore('ideas').delete(id);
    return new Promise(resolve => { tx.oncomplete = () => resolve(); });
  }

  async archiveIdea(id: string, archive: boolean): Promise<void> {
     const tx = await this.getTransaction(['ideas'], 'readwrite');
     const store = tx.objectStore('ideas');
     store.get(id).onsuccess = (e: any) => {
         const idea = e.target.result;
         if (idea) store.put({ ...idea, isArchived: archive });
     };
     return new Promise(resolve => { tx.oncomplete = () => resolve(); });
  }

  async getIdeaVersions(ideaId: string): Promise<IdeaVersion[]> { return []; }

  async getUserProfile(userId: string, email: string): Promise<UserProfile> {
      const tx = await this.getTransaction(['users'], 'readwrite');
      const store = tx.objectStore('users');
      return new Promise(resolve => {
          store.get(userId).onsuccess = (e: any) => {
              if (e.target.result) resolve(e.target.result);
              else {
                  const p: UserProfile = { uid: userId, email, plan: 'free', ideasCreated: 0, tokensUsed: 0, subscriptionStatus: 'active', periodEnd: Date.now() + 99999999 };
                  store.put(p);
                  resolve(p);
              }
          }
      });
  }

  async getMonthlyAllowance(userId: string): Promise<MonthlyAllowance> {
      // Mock for offline
      return { limit: 10000, used: 0, plan: 'free', resetAt: Date.now() };
  }

  async updateUserProfile(userId: string, data: Partial<UserProfile>): Promise<void> {
      const tx = await this.getTransaction(['users'], 'readwrite');
      const store = tx.objectStore('users');
      store.get(userId).onsuccess = (e: any) => {
          if (e.target.result) store.put({ ...e.target.result, ...data });
      }
  }

  async incrementUsage(userId: string, type: string, amount: number): Promise<void> {
      const tx = await this.getTransaction(['users'], 'readwrite');
      const store = tx.objectStore('users');
      store.get(userId).onsuccess = (e: any) => {
          const p = e.target.result as UserProfile;
          if (p) {
              if (type === 'ideas') p.ideasCreated += amount;
              if (type === 'tokens') p.tokensUsed += amount;
              store.put(p);
          }
      }
  }

  async upgradeUserPlan(userId: string, plan: PlanTier): Promise<MonthlyAllowance> {
      // Mock IDB implementation
      const p = await this.getUserProfile(userId, 'mock');
      p.plan = plan;
      await this.updateUserProfile(userId, { plan });
      return { limit: PLAN_LIMITS[plan].credits, used: 0, plan, resetAt: Date.now() };
  }
}

// Provider Switching
const firebaseProvider = new FirebaseProvider();
const idbProvider = new IndexedDBProvider();
const getProvider = () => auth.currentUser ? firebaseProvider : idbProvider;

export const getIdeas = () => getProvider().getIdeas();
export const getPublicIdeas = () => getProvider().getPublicIdeas();
export const saveIdea = (idea: Idea) => getProvider().saveIdea(idea);
export const deleteIdea = (id: string) => getProvider().deleteIdea(id);
export const archiveIdea = (id: string, archive?: boolean) => getProvider().archiveIdea(id, archive ?? true);
export const getIdeaVersions = (ideaId: string) => getProvider().getIdeaVersions(ideaId);

export const getUserProfile = (userId: string, email: string) => getProvider().getUserProfile(userId, email);
export const getMonthlyAllowance = (userId: string) => getProvider().getMonthlyAllowance(userId);
export const updateUserProfile = (userId: string, data: Partial<UserProfile>) => getProvider().updateUserProfile(userId, data);
export const incrementUsage = (userId: string, type: 'ideas' | 'tokens', amount: number) => getProvider().incrementUsage(userId, type, amount);
export const upgradeUserPlan = (userId: string, plan: PlanTier) => getProvider().upgradeUserPlan(userId, plan);

export const syncGuestIdeasToFirebase = async (user: User) => {
    const idb = new IndexedDBProvider();
    const guestIdeas = await idb.getIdeas();
    
    if (guestIdeas.length === 0) return;

    const fb = new FirebaseProvider();
    
    // Save sequentially to ensure they are safely transferred
    for (const idea of guestIdeas) {
        // Prepare idea for transfer: assign new userId
        const ideaToSave = { ...idea, userId: user.uid };
        
        try {
            // Save to FB
            await fb.saveIdea(ideaToSave);
            
            // Delete from IDB after successful save to avoid duplication or confusion in hybrid state
            await idb.deleteIdea(idea.id);
        } catch (e) {
            console.error("Failed to sync idea", idea.id, e);
            // Continue syncing others even if one fails
        }
    }
};
